'use client';

import { useEffect, useRef, useState } from 'react';

const processSteps = [
  {
    number: '01',
    title: 'Discover',
    description: 'Deep audit of your funnel, audience, and gaps. Zero assumptions - only data.',
    details: ['Brand audit', 'Competitor analysis', 'Funnel mapping', 'Audience research'],
  },
  {
    number: '02',
    title: 'Strategize',
    description: 'Custom growth blueprint built around your revenue goals - never templates.',
    details: ['Channel strategy', 'Budget allocation', 'KPI setting', 'Roadmap'],
  },
  {
    number: '03',
    title: 'Execute',
    description: 'Creative, campaigns, and conversion systems deployed as one connected engine.',
    details: ['Campaign builds', 'Content systems', 'Landing pages', 'Tracking'],
  },
  {
    number: '04',
    title: 'Optimize',
    description: 'Every signal is measured, tested, and converted into better performance.',
    details: ['Experimentation', 'CRO', 'Reporting', 'Efficiency gains'],
  },
  {
    number: '05',
    title: 'Scale',
    description: 'Proven growth loops expand with control, clarity, and compounding returns.',
    details: ['Growth loops', 'Market expansion', 'Automation', 'Revenue lift'],
  },
];

export default function TimelineSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef([]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveIndex(Number(entry.target.dataset.index));
          }
        });
      },
      { rootMargin: '-42% 0px -42% 0px', threshold: 0.05 }
    );

    itemRefs.current.forEach((item) => {
      if (item) observer.observe(item);
    });

    return () => observer.disconnect();
  }, []);

  const progress = (activeIndex / (processSteps.length - 1)) * 100;

  return (
    <section className="operate-section" data-theme="dark">
      <div className="operate-color-wash" aria-hidden="true" />

      <div className="operate-shell">
        <header className="operate-heading">
          <p className="operate-label">How We Operate</p>
          <h2 className="operate-title">
            A system, not a
            <br />
            <span>guessing game</span>
          </h2>
        </header>

        <div className="roadmap">
          <div className="roadmap-track" aria-hidden="true">
            <div className="roadmap-fill" style={{ height: `${progress}%` }} />
          </div>

          {processSteps.map((step, index) => (
            <article
              key={step.number}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              data-index={index}
              className={`stage-row ${index % 2 === 0 ? 'stage-left' : 'stage-right'} ${
                activeIndex === index ? 'is-active' : ''
              }`}
            >
              <div className="stage-node">
                <span>{step.number}</span>
              </div>
              <div className="stage-card">
                <h3>{step.title}</h3>
                <p>{step.description}</p>
                <div className="stage-details">
                  {step.details.map((detail) => (
                    <span key={detail}>{detail}</span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <style jsx global>{`
        .operate-section {
          --pitch-black: #080400;
          --deep-twilight: #00145b;
          --cobalt-blue: #0047ab;
          --true-azure: #3730a3;
          --chartreuse: #ceff1a;
          --white: #fbfdfd;
          --card: rgba(9, 12, 26, 0.78);
          --border: rgba(251, 253, 253, 0.1);
          position: relative;
          isolation: isolate;
          overflow: hidden;
          min-height: 100vh;
          margin-top: -1px;
          padding: clamp(7rem, 10vw, 10rem) clamp(1.25rem, 5vw, 6rem) clamp(8rem, 12vw, 12rem);
          color: var(--white);
          background: #080400;
        }

        .operate-shell {
          position: relative;
          z-index: 2;
          width: min(1480px, 100%);
          margin: 0 auto;
        }

        .operate-color-wash {
          display: none;
        }

        .operate-heading {
          max-width: 780px;
          margin: 0 auto clamp(4rem, 8vw, 7rem);
          text-align: center;
        }

        .operate-label {
          margin: 0 0 1.6rem;
          color: rgba(206, 255, 26, 0.9);
          font: 600 0.8rem/1 var(--font-inter), sans-serif;
          letter-spacing: 0.48em;
          text-transform: uppercase;
        }

        .operate-title {
          margin: 0;
          font: 700 clamp(2.6rem, 4.4vw, 4.6rem) / 1.15 var(--font-clash), sans-serif;
          letter-spacing: 0.02em;
        }

        .operate-title span {
          color: transparent;
          background: linear-gradient(90deg, var(--true-azure) 0%, var(--cobalt-blue) 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .roadmap {
          position: relative;
          max-width: 1320px;
          margin: 0 auto;
          padding-top: 0.4rem;
        }

        .roadmap-track {
          position: absolute;
          top: 3.1rem;
          bottom: 3.1rem;
          left: 50%;
          width: 2px;
          transform: translateX(-50%);
          background: rgba(251, 253, 253, 0.1);
        }

        .roadmap-fill {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          min-height: 0;
          background: linear-gradient(180deg, rgba(55, 48, 163, 0.95), rgba(0, 71, 171, 0.88) 60%, rgba(206, 255, 26, 0.8));
          box-shadow: 0 0 12px rgba(0, 71, 171, 0.34);
          transition: height 700ms ease;
        }

        .stage-row {
          position: relative;
          display: grid;
          grid-template-columns: minmax(0, 1fr) 112px minmax(0, 1fr);
          min-height: clamp(16rem, 20vw, 20rem);
          align-items: start;
        }

        .stage-node {
          z-index: 2;
          grid-column: 2;
          grid-row: 1;
          place-self: start center;
          display: grid;
          place-items: center;
          width: 4.9rem;
          height: 4.9rem;
          border: 1px solid rgba(55, 48, 163, 0.82);
          border-radius: 50%;
          color: rgba(55, 48, 163, 0.85);
          background: #090c19;
          box-shadow: 0 0 22px rgba(55, 48, 163, 0.2);
          transition: border-color 400ms ease, box-shadow 400ms ease, color 400ms ease;
        }

        .stage-node span {
          font: 700 0.95rem/1 ui-monospace, SFMono-Regular, Menlo, monospace;
        }

        .stage-row.is-active .stage-node {
          color: var(--chartreuse);
          border-color: var(--chartreuse);
          background: #090d17;
          box-shadow: 0 0 0 5px rgba(206, 255, 26, 0.06), 0 0 24px rgba(206, 255, 26, 0.2);
        }

        .stage-card {
          grid-row: 1;
          max-width: 570px;
          border: 1px solid var(--border);
          border-radius: 1.3rem;
          padding: clamp(1.6rem, 3vw, 2.2rem);
          color: rgba(251, 253, 253, 0.67);
          background: var(--card);
          backdrop-filter: blur(16px);
          box-shadow: 0 20px 55px rgba(0, 0, 0, 0.22);
          opacity: 0.84;
          transform: translateY(0.75rem);
          transition: border-color 400ms ease, box-shadow 400ms ease, transform 400ms ease, opacity 400ms ease;
        }

        .stage-left .stage-card {
          grid-column: 1;
          justify-self: end;
          text-align: right;
        }

        .stage-right .stage-card {
          grid-column: 3;
          justify-self: start;
          text-align: left;
        }

        .stage-row.is-active .stage-card {
          opacity: 1;
          transform: translateY(0);
          border-color: rgba(206, 255, 26, 0.3);
          background:
            linear-gradient(120deg, rgba(0, 20, 91, 0.3), rgba(9, 12, 26, 0.82) 38%),
            var(--card);
          box-shadow: 0 0 0 1px rgba(206, 255, 26, 0.035), 0 24px 68px rgba(0, 0, 0, 0.27);
        }

        .stage-card h3 {
          margin: 0 0 1rem;
          color: rgba(55, 48, 163, 0.92);
          font: 700 clamp(1.55rem, 2.4vw, 2rem) / 1.2 var(--font-inter), sans-serif;
          transition: color 400ms ease;
        }

        .stage-row.is-active .stage-card h3 {
          color: var(--chartreuse);
        }

        .stage-card p {
          margin: 0 0 1.35rem;
          font: 400 clamp(1rem, 1.45vw, 1.13rem) / 1.7 var(--font-inter), sans-serif;
        }

        .stage-details {
          display: flex;
          flex-wrap: wrap;
          gap: 0.35rem 0.75rem;
          color: rgba(251, 253, 253, 0.38);
          font: 500 0.78rem/1.7 ui-monospace, SFMono-Regular, Menlo, monospace;
        }

        .stage-left .stage-details {
          justify-content: flex-end;
        }

        .stage-details span:not(:last-child)::after {
          content: ' .';
          padding-left: 0.75rem;
          color: rgba(206, 255, 26, 0.45);
        }

        @media (max-width: 860px) {
          .operate-section {
            padding-top: 5rem;
          }

          .operate-label {
            letter-spacing: 0.32em;
          }

          .roadmap {
            padding-left: 3.35rem;
          }

          .roadmap-track {
            left: 1.45rem;
          }

          .stage-row {
            display: block;
            min-height: 0;
            padding-bottom: 2.5rem;
          }

          .stage-node {
            position: absolute;
            left: -3.35rem;
            top: 0;
            width: 2.9rem;
            height: 2.9rem;
          }

          .stage-card,
          .stage-left .stage-card,
          .stage-right .stage-card {
            max-width: none;
            text-align: left;
            padding: 1.35rem;
          }

          .stage-left .stage-details {
            justify-content: flex-start;
          }
        }
      `}</style>
    </section>
  );
}
